import React, { useCallback, useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { Model } from "survey-core";
import { KashSurveyComponent } from "../../components/kash-survey.component";
import {
  getAssessmentQuestions,
  submitSurveyAnswers,
  windowEvent,
  fetchWindowEvent,
  ActivityLogAction,
  GetAssessmentQuestionsResponse,
} from "../../network/network";
import "./quiz.screen.css";
import _ from "lodash";
import { confirm } from "../../../../components/modal/comfirm";
import { ROOT_PATH } from "../../../../routes/constants";

// Extend Window interface for React Native WebView
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

/**
 * KASH Assessment Quiz Screen
 *
 * This component renders the KASH (Knowledge, Attitude, Skills, Habits) assessment
 * using SurveyJS library for interactive survey functionality.
 *
 * Features:
 * - Fetches assignment detail to get surveyId and existing answers
 * - Determines readonly mode based on assignment status and user permissions
 * - Supports both interactive and readonly modes
 * - Pre-fills existing answers from assignment data
 */
export const KashAssessmentQuizScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { assignmentId } = useParams<{
    assignmentId: string;
  }>();

  // Extract readonly mode from query parameters (set by WebView)
  const queryParams = new URLSearchParams(location.search);
  const readonlyParam = queryParams.get("readonly") === "true";

  // Get language from session storage (passed from React Native)
  const appLanguage = window.sessionStorage.getItem("APP_LANGUAGE") || "en";

  const [surveyData, setSurveyData] = useState<GetAssessmentQuestionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [readonly, setReadonly] = useState(readonlyParam);
  const [isCompleted, setIsCompleted] = useState(false);
  const [hasPopulatedAnswers, setHasPopulatedAnswers] = useState(false);
  const surveyModelRef = useRef<Model | null>(null);

  /**
   * Map React Native app language code to Web i18n language code
   * App uses: zh_HK, zh_CN, en, etc.
   * Web uses: zhhk, zh-Hant, en, etc.
   */
  const mapAppLanguageToWebLanguage = (appLang: string): string => {
    const langMapping: Record<string, string> = {
      zh_HK: "zhhk",
      "zh-HK": "zhhk",
      zh_CN: "zh-cn",
      "zh-CN": "zh-cn",
      en: "en",
      id: "id",
      my: "my",
      th: "th",
      vi: "vi",
      km_KH: "km",
      "km-KH": "km",
    };
    return langMapping[appLang] || appLang.toLowerCase();
  };

  /**
   * Set global language for the entire web app
   */
  useEffect(() => {
    if (appLanguage) {
      const webLanguage = mapAppLanguageToWebLanguage(appLanguage);
      if (i18n.language !== webLanguage) {
        i18n.changeLanguage(webLanguage);
      }
    }
  }, [appLanguage]);

  /**
   * Wait for AUTH_TOKEN to be available in session storage
   */
  const waitForToken = async (timeoutMs = 5000): Promise<string> => {
    const start = Date.now();
    console.time("waitForToken");

    while (true) {
      const token = sessionStorage.getItem("AUTH_TOKEN");

      if (token) {
        return token;
      }

      if (Date.now() - start > timeoutMs) {
        console.timeEnd("waitForToken");
        throw new Error("Token tidak tersedia dalam waktu yang ditentukan");
      }

      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  };

  /**
   * Load assessment questions and initialize survey
   */
  const loadAssessment = useCallback(async () => {
    if (!assignmentId) {
      setError("Assignment ID is required");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await waitForToken(); // Ensure AUTH_TOKEN is available before making API calls

      // Get assignment detail, surveyId, assessment template, and determine readonly mode
      const response: GetAssessmentQuestionsResponse = await getAssessmentQuestions(assignmentId);

      setSurveyData(response);
      const _readonly = response.isReadonly || readonlyParam;
      setReadonly(_readonly);

      // Check if there are any existing answers to set hasPopulatedAnswers
      if (response.answers && Object.keys(response.answers).length > 0) {
        setHasPopulatedAnswers(true);
        // Send initial populated state to React Native
        if (window.ReactNativeWebView && !_readonly) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              action: "SURVEY_POPULATED",
              payload: {
                hasAnswers: true,
                answers: response.answers,
              },
            }),
          );
        }
      }

      // Notify React Native that assessment is loaded and ready
      setTimeout(() => {
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              action: "ASSESSMENT_LOADED",
              payload: {
                assignmentId,
                surveyId: response.assessmentId,
                readonly: _readonly,
                hasAnswers: response.answers && Object.keys(response.answers).length > 0,
              },
            }),
          );
        }
      }, 100);

      // Log "viewed" event when assessment is loaded
      // Only log if user has edit permission (not readonly mode)
      if (response.isReadonly === false && !readonlyParam) {
        windowEvent(assignmentId, {
          activityLogAction: ActivityLogAction.viewed,
        });
      }
    } catch (err) {
      console.error("Failed to load assessment:", err);
      setError(err instanceof Error ? err.message : "Failed to load assessment");
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            action: "ASSESSMENT_ERROR",
            payload: {
              assignmentId,
              error: err instanceof Error ? err.message : "Failed to load assessment",
            },
          }),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [assignmentId, readonlyParam]);

  /**
   * Handle survey submission
   */
  const handleSurveySubmit = useCallback(
    async (data: any): Promise<any> => {
      if (!assignmentId || !surveyData) {
        setError(t("Incentive.errors.missing_assignment_or_survey_data"));
        throw new Error(t("Incentive.errors.missing_assignment_or_survey_data"));
      }

      if (readonly) {
        setError(t("Incentive.errors.readonly_mode_submit"));
        throw new Error(t("Incentive.errors.readonly_mode_submit"));
      }

      try {
        // Ask for confirmation before submitting
        const confirmResult = await confirm(
          {
            title: t("Submit"),
            message: t("Incentive.submit_assignment_confirmation"),
            confirmButtonText: t("app.button.submit"),
            cancelButtonText: t("Cancel"),
          },
          {
            backdropClose: false, // Disable backdrop click to close modal
          } as any,
        );

        if (!confirmResult || !confirmResult.ok) {
          // User cancelled submission
          return Promise.reject(new Error(t("Incentive.errors.submission_cancelled_by_user")));
        }

        setSubmitting(true);

        // Submit survey answers using real API
        const response = await submitSurveyAnswers(surveyData.assessmentId, assignmentId, data);

        if (response.success) {
          if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({
                action: "SURVEY_SUBMIT",
                payload: { succeed: true, submittedAt: response.submittedAt },
              }),
            );
          }
          // Mark as completed to prevent logging close events
          setIsCompleted(true);

          // Navigate to completion page
          navigate(`${ROOT_PATH}/mdrt/kash-assessment-quiz/${assignmentId}/complete`, {
            state: {
              submittedAt: response.submittedAt,
              insightsAvailable: response.insightsAvailable,
            },
          });
          return response;
        } else {
          throw new Error(t("Incentive.errors.failed_submit_assesment_try"));
        }
      } catch (err) {
        console.error("Failed to submit assessment:", err);
        setError(
          err instanceof Error ? err.message : t("Incentive.errors.failed_submit_assesment"),
        );
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [assignmentId, surveyData, readonly, navigate],
  );

  /**
   * Get unanswered question titles from the current page
   */
  const getUnansweredQuestionTitles = useCallback((): string => {
    if (!surveyModelRef.current?.currentPage) {
      return "";
    }

    const currentPage = surveyModelRef.current.currentPage;
    const unansweredTitles: string[] = [];

    currentPage.questions.forEach((question: any) => {
      // Check if question is unanswered (value is undefined or null)
      if (question.value === undefined || question.value === null || question.value === "") {
        // Use question title or name as fallback
        const title = question.title || question.name;
        unansweredTitles.push(title);
      }
    });

    return unansweredTitles.join(", ");
  }, []);

  /**
   * Handle component cleanup and activity logging
   * Log survey activity when user leaves the page (close/blur)
   */
  useEffect(() => {
    if (!assignmentId || !surveyData) {
      return;
    }

    const userAgent = navigator.userAgent;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      userAgent,
    );

    const handleTabClose = (event: any) => {
      event.preventDefault();

      // Only log close event if user has edit permission (not readonly mode)
      if (!isCompleted && !readonly) {
        // Get unanswered question titles from current page
        const unansweredQuestions = getUnansweredQuestionTitles();

        const eventData = {
          activityLogAction: ActivityLogAction.closedWithoutAnswer,
          activityLogLastQuestion: unansweredQuestions || undefined,
        };

        // Send postMessage to React Native WebView
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              action: "SURVEY_CLOSE_EVENT",
              payload: eventData,
            }),
          );
        }

        // Use fetchWindowEvent with keepalive to ensure request completes even if page closes
        fetchWindowEvent(assignmentId, eventData);
      }
    };

    // Mobile devices: use 'blur' event
    // Desktop: use 'beforeunload' event (more reliable than 'unload')
    const eventType = isMobile ? "blur" : "beforeunload";
    window.addEventListener(eventType, handleTabClose);

    return () => {
      window.removeEventListener(eventType, handleTabClose);
    };
  }, [assignmentId, surveyData, isCompleted, getUnansweredQuestionTitles]);

  /**
   * Load assessment on component mount
   */
  useEffect(() => {
    loadAssessment();
  }, [loadAssessment]);

  /**
   * Handle retry loading
   */
  const handleRetry = useCallback(() => {
    loadAssessment();
  }, [loadAssessment]);

  /**
   * Handle navigation back
   */
  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 border-4 border-red-200 border-t-red-600 rounded-full animate-spin mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {t("Incentive.loading_assessment")}
        </h2>
        <p className="text-gray-600">{t("Incentive.loading_assessment_message")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-4">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            {t("Incentive.unable_to_load_assessment")}
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-x-4">
            <button
              onClick={handleRetry}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400"
            >
              {t("please_try_again")}
            </button>
            <button
              onClick={handleGoBack}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              {t("back")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!surveyData?.schema) {
    return (
      <div className="m-4">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t("Incentive.assessment_not_available")}
          </h2>
          <p className="text-gray-600 mb-6">{t("Incentive.assessment_not_initialized")}</p>
          <button
            onClick={handleGoBack}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            {t("Common.go_back")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Readonly Mode Indicator */}
      {/* {readonly && (
        <div className="m-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-800">
                <strong>Read-only mode:</strong> This assessment has been submitted or you are viewing as a mentor.
              </p>
            </div>
          </div>
        </div>
      )} */}

      <div className="relative">
        {submitting && (
          <div className="fixed inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-red-200 border-t-red-600 rounded-full animate-spin mx-auto mb-2" />
              <p className="text-gray-700">{t("Incentive.submitting_assessment")}</p>
            </div>
          </div>
        )}

        {/* @ts-ignore - React type compatibility issue */}
        <KashSurveyComponent
          json={surveyData.schema}
          onSubmit={handleSurveySubmit}
          defaultValue={surveyData.answers}
          disabled={readonly || submitting}
          language={appLanguage}
          onSurveyCreated={(model: Model) => {
            surveyModelRef.current = model;

            // Listen to survey value changes to detect when user populates any field
            model.onValueChanged.add((sender: Model, options: any) => {
              // Check if survey has any answers (user has started filling)
              const currentAnswers = sender.data;
              const hasAnswers = currentAnswers && Object.keys(currentAnswers).length > 0;

              // Only update and send message if state changed from false to true
              if (hasAnswers && !hasPopulatedAnswers) {
                setHasPopulatedAnswers(true);

                // Send message to React Native WebView
                if (window.ReactNativeWebView && !readonly) {
                  window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                      action: "SURVEY_POPULATED",
                      payload: {
                        hasAnswers: true,
                        answers: currentAnswers,
                      },
                    }),
                  );
                }
              }
            });
          }}
        />
      </div>
    </div>
  );
};
